import { useTranslation } from 'hooks/useTranslation';
import Accordion from 'components/ui/ContentBlocks/Accordion';
import QunoscoreDetail from './QunoscoreDetail';
import type { QunoscoreEntry, QunoscoreIndex, Theme } from './qunoscoreData';
import type { DoctorProfileEntry } from '.';

type Props = {
  block: QunoscoreEntry;
  fields: DoctorProfileEntry;
};

const QunoscoreBlock = ({ block, fields }: Props): JSX.Element => {
  const { title, description, indexes } = block;
  const { t } = useTranslation();

  const jsonFields = [
    'topRankedUniversity',
    'treatmentsPerformed',
    'treatmentsPerformedLastYear',
    'TPCAllTime',
    'TPCLastYear',
    'yearsOfExperienceAsDoctor',
    'relevantSpecialization',
    'scientificActivity',
    'specialistSociety',
    'appointmentAvailability',
    'responsiveness',
    'englishSpoken',
    'priceLevel',
    'discountForFollowUpTreatment',
    'discountForCompanion',
    'accommodationIncluded',
    'afterCareIncluded',
    'qunoReviews',
    'publicReviews',
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getValue = (id: string): any => {
    return jsonFields.includes(id)
      ? fields?.qunoscoreDetails?.[
          id as keyof typeof fields.qunoscoreDetails
        ]?.['en-US']
      : fields[id as keyof DoctorProfileEntry];
  };

  const getScoreStatus = (
    index: QunoscoreIndex,
  ): { theme?: Theme; min?: number } => {
    const { criteria, criteriaType, dataType, comparison } = index;

    const value = getValue(index.id);
    if (!criteria) {
      return {
        theme: 'excellent',
        min: 1,
      };
    }

    if (criteriaType) {
      if (criteriaType === 'percentile') {
        let result;
        let formattedValue = value;

        let formattedComparison = getValue(comparison as string) as number;

        let minResult;

        if (dataType && dataType === 'years') {
          formattedValue = new Date().getFullYear() - value;
          formattedComparison = new Date().getFullYear() - formattedComparison;
        }

        const percent = (formattedValue * 100) / formattedComparison;
        Object.keys(index.criteria).forEach((range) => {
          const minMax = range.split('-');
          if (
            percent >= parseInt(minMax[0]) &&
            percent <= parseInt(minMax[1])
          ) {
            result = index.criteria[range];
          }
        });

        Object.keys(index.min).forEach((range) => {
          const minMax = range.split('-');
          if (
            percent >= parseInt(minMax[0]) &&
            percent <= parseInt(minMax[1])
          ) {
            minResult = index.min[range];
          }
        });

        return {
          theme: result,
          min: minResult,
        };
      }

      if (criteriaType === 'range') {
        let result;
        let minResult;
        let formattedValue = value;

        if (dataType && dataType === 'years') {
          formattedValue = new Date().getFullYear() - value;
        }

        Object.keys(index.criteria).forEach((range) => {
          const minMax = range.split('-');
          if (minMax[1] === '.' && formattedValue >= parseFloat(minMax[0])) {
            result = index.criteria[range];
          } else if (
            formattedValue >= parseFloat(minMax[0]) &&
            formattedValue <= parseFloat(minMax[1])
          ) {
            result = index.criteria[range];
          }
        });

        Object.keys(index.min).forEach((range) => {
          const minMax = range.split('-');
          if (minMax[1] === '.' && formattedValue >= parseFloat(minMax[0])) {
            minResult = index.min[range];
          } else if (
            formattedValue >= parseFloat(minMax[0]) &&
            formattedValue <= parseFloat(minMax[1])
          ) {
            minResult = index.min[range];
          }
        });

        return {
          theme: result,
          min: minResult,
        };
      }
    }

    return {
      theme: index.criteria[value],
      min: index.min ? index.min[value] : 1,
    };
  };

  return (
    <Accordion.Item title={t(title)}>
      <div className="mt-4 md:pr-8">
        <p className="mb-4">{t(description)}</p>
        {indexes.map((index) => {
          const { dataType } = index;
          const value = getValue(index.id);

          if (typeof value === 'undefined') {
            return (
              <QunoscoreDetail
                key={index.id}
                label={t(index.label)}
                value={'n/a'}
                keys={index.keys}
                isNotAvailable
              />
            );
          }

          return (
            <QunoscoreDetail
              key={index.id}
              label={t(index.label)}
              value={value}
              min={getScoreStatus(index).min}
              max={index.max}
              theme={getScoreStatus(index).theme}
              formatYear={dataType === 'years'}
              keys={index.keys}
            />
          );
        })}
      </div>
    </Accordion.Item>
  );
};

export default QunoscoreBlock;
